import { useQuery } from '@apollo/client'
import { GET_USER_PROFILE_QUERY } from '../query/gqlQueries'

// Reads the learner's profile and turns it into a personalisation block for
// generation prompts — the "make it personal to remember" principle. Returns the
// raw profile too, plus the mother tongue, so callers can also set translation
// framing. Safe when signed out or the profile is empty (returns '').
export default function useLearnerContext(username) {
  // errorPolicy 'ignore' so a backend that hasn't shipped getUserProfile yet
  // degrades to "no personalisation" instead of surfacing a validation error.
  const { data } = useQuery(GET_USER_PROFILE_QUERY, { variables: {}, skip: !username, errorPolicy: 'ignore' })
  const profile = data?.getUserProfile || null
  const user = profile?.user || null
  const native = profile?.native || []
  const learning = profile?.learning || []
  const nativeName = native[0]?.name || 'English'

  const parseJson = () => { try { return JSON.parse(user?.profile || '{}') || {} } catch { return {} } }

  // A short, natural-language brief the model can weave into examples. `targetLangName`
  // is the language being taught (e.g. "Turkish") so we can quote the right level.
  const personalize = (targetLangName) => {
    if (!user) return ''
    const pj = parseJson()
    const facts = []
    if (user.name) facts.push(`their name is ${user.name}`)
    if (user.birthYear) facts.push(`they are about ${new Date().getFullYear() - user.birthYear}`)
    if (user.occupation) facts.push(`they are a ${user.occupation}`)
    const where = [user.homeCity, user.homeCountry].filter(Boolean).join(', ')
    if (where) facts.push(`they live in ${where}`)
    const hobbies = Array.isArray(pj.hobbies) ? pj.hobbies.filter(Boolean) : []
    if (hobbies.length) facts.push(`they enjoy ${hobbies.join(', ')}`)
    if (pj.goals) facts.push(`they are learning because: ${pj.goals}`)

    const lvl = (learning.find(l => l.name === targetLangName) || {}).level
    let out = ''
    if (facts.length) {
      out += `LEARNER — personalise the examples naturally (use their name; weave their life in only where it fits, never force it): ${facts.join('; ')}.`
    }
    if (nativeName) out += ` Their mother tongue is ${nativeName}; give English (or ${nativeName}) translations accordingly.`
    if (lvl) out += ` Their ${targetLangName} level is ${lvl} — pitch difficulty to that.`
    return out.trim()
  }

  return { profile, user, native, learning, nativeName, personalize }
}
